<template>
  <div class="app-container">
    <h1>Płatności</h1>

    <div class="filter-container">
      <el-input
        v-model="query.search"
        style="width: 260px;"
        class="filter-item"
        placeholder="Numer, e-mail lub nazwa klienta"
        clearable
        @clear="handleFilter"
        @keyup.enter.native="handleFilter"
      />
      <el-select
        v-model="query.status"
        class="filter-item"
        placeholder="Status"
        clearable
        style="width: 180px;"
        @change="handleFilter"
      >
        <el-option
          v-for="item in statusOptions"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        />
      </el-select>
      <el-date-picker
        v-model="dateRange"
        class="filter-item"
        type="daterange"
        range-separator="-"
        start-placeholder="Data od"
        end-placeholder="Data do"
        format="yyyy-MM-dd"
        value-format="yyyy-MM-dd"
        unlink-panels
        @change="handleFilter"
      />
      <el-button v-waves class="filter-item" type="primary" icon="el-icon-search" @click="handleFilter">
        Szukaj
      </el-button>
    </div>

    <el-table
      v-loading="loading"
      :data="list"
      :row-class-name="rowClass"
      border
      fit
      highlight-current-row
      style="width: 100%"
    >
      <el-table-column align="center" label="Data" width="160">
        <template slot-scope="scope">
          <span>{{ scope.row.created_at }}</span>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Numer" width="130">
        <template slot-scope="scope">
          <span>{{ scope.row.number }}</span>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Klient">
        <template slot-scope="scope">
          <span v-if="scope.row.user">{{ scope.row.user.email }}</span>
          <span v-else>-</span>
          <br>
          <span class="client-name">{{ clientName(scope.row) }}</span>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Oferta">
        <template slot-scope="scope">
          <a v-if="scope.row.offer" :href="offerUrl(scope.row.offer.slug)" target="_blank">
            {{ scope.row.offer.title }}
          </a>
          <span v-else>-</span>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Opis">
        <template slot-scope="scope">
          <span>{{ scope.row.description }}</span>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Kwota" width="120">
        <template slot-scope="scope">
          <span>{{ formatAmount(scope.row.total) }} PLN</span>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Status" width="180">
        <template slot-scope="scope">
          <el-tag :type="statusTag(scope.row.status)">
            {{ statusLabel(scope.row.status) }}
          </el-tag>
          <!--
            Płatność „oczekująca" starsza niż godzina to sygnał, że klient mógł
            zapłacić, a callback z TPay nie doszedł - taki wiersz jest wyróżniony.
          -->
          <div v-if="isStalePending(scope.row)" class="stale-hint">
            brak potwierdzenia > 1 godz.
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- `page-sizes` musi zawierać domyślne 25, inaczej el-pagination pokazuje pusty wybór. -->
    <pagination
      v-show="total > 0"
      :total="total"
      :page.sync="query.page"
      :limit.sync="query.limit"
      :page-sizes="[10, 25, 50, 100]"
      @pagination="getList"
    />
  </div>
</template>

<script>
import Pagination from '@/components/Pagination'; // Secondary package based on el-pagination
import TransactionResource from '@/api/transaction';
import waves from '@/directive/waves'; // Waves directive

const transactionResource = new TransactionResource();

const ONE_HOUR_MS = 60 * 60 * 1000;

export default {
  name: 'TransactionsList',
  components: { Pagination },
  directives: { waves },
  data() {
    return {
      loading: false,
      total: 0,
      list: [],
      dateRange: null,
      statusOptions: [
        { value: 'paid', label: 'Opłacona' },
        { value: 'pending', label: 'Oczekująca' },
        { value: 'canceled', label: 'Anulowana' },
      ],
      query: {
        page: 1,
        limit: 25,
        search: '',
        status: '',
      },
    };
  },
  computed: {
    frontendUrl() {
      return process.env.VUE_APP_FRONTEND_URL || 'https://dazu.pl';
    },
  },
  created() {
    this.getList();
  },
  methods: {
    offerUrl(slug) {
      return this.frontendUrl + '/ogloszenia/' + slug;
    },
    clientName(row) {
      // `user.name` to pełne "imię nazwisko" z profilu; gdy profil jest pusty,
      // zostaje nazwa zapisana na transakcji w chwili zakupu.
      if (row.user && row.user.name) {
        return row.user.name;
      }
      return row.name || '-';
    },
    formatAmount(total) {
      // Kwoty w bazie trzymane są w groszach.
      return (Number(total || 0) / 100).toFixed(2);
    },
    statusLabel(status) {
      const option = this.statusOptions.find(item => item.value === status);
      return option ? option.label : status;
    },
    statusTag(status) {
      if (status === 'paid') {
        return 'success';
      }
      if (status === 'pending') {
        return 'warning';
      }
      return 'info';
    },
    isStalePending(row) {
      // created_at_iso ma offset strefy serwera - "created_at" (bez strefy) byłoby
      // parsowane w strefie lokalnej przeglądarki i przy progu 1h przekłamywało wiek.
      const timestamp = row.created_at_iso || (row.created_at ? String(row.created_at).replace(' ', 'T') : null);
      if (row.status !== 'pending' || !timestamp) {
        return false;
      }
      const createdAt = new Date(timestamp);
      if (isNaN(createdAt.getTime())) {
        return false;
      }
      return Date.now() - createdAt.getTime() > ONE_HOUR_MS;
    },
    rowClass({ row }) {
      return this.isStalePending(row) ? 'warning-row' : '';
    },
    handleFilter() {
      this.query.page = 1;
      this.getList();
    },
    async getList() {
      this.loading = true;
      try {
        const params = {
          page: this.query.page,
          per_page: this.query.limit,
        };
        if (this.query.search) {
          params.search = this.query.search;
        }
        if (this.query.status) {
          params.status = this.query.status;
        }
        if (this.dateRange && this.dateRange.length === 2) {
          params.date_from = this.dateRange[0];
          params.date_to = this.dateRange[1];
        }

        const { data, total } = await transactionResource.list(params);
        this.list = data;
        this.total = total;
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.el-table .warning-row {
  background: oldlace;
}
.client-name {
  color: #909399;
  font-size: 12px;
}
.stale-hint {
  color: #e6a23c;
  font-size: 11px;
  margin-top: 4px;
}
.app-container {
  flex: 1;
  justify-content: space-between;
  font-size: 14px;
  padding-right: 8px;
  .filter-container {
    margin-bottom: 10px;
    .filter-item {
      margin-right: 10px;
    }
  }
  .block {
    float: left;
    min-width: 250px;
  }
  .clear-left {
    clear: left;
  }
}
</style>
